import { Box } from '@mui/material'
import { Fragment } from 'react'

import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { FONT_TABULAR } from '@/shared/styles/typography'

import type { PatientAttribute } from '../../types'
import { formatMonthDay } from '../../utils/formatPrepDate'
import { EmptyLine } from '../GuidancePrepCard/parts'

interface Props {
  attributes: PatientAttribute[]
  patient: {
    sex: '男' | '女' | null
    age: number | null
  }
}

// 対象にならない属性. [未取得] と区別して「—（理由）」で出す (画面案と同じ).
const notApplicableReason = (
  attribute: PatientAttribute,
  patient: Props['patient']
): string | null => {
  if (attribute.key === 'pregnancy' && patient.sex === '男') return '男性'
  if (attribute.key === 'weight' && patient.age !== null && patient.age >= 15) {
    return '15 歳以上'
  }
  return null
}

// 1 行 = 値 + 出所. 出所は右の列に「M/D 頭書き」「M/D 会話」の 1 つの書き方で揃える
// (日付が先. 値の後ろに続けて書かない. hub design-book ADR-0061).
type Source = { date: string | null; kind: '頭書き' | '会話' }

type Line = {
  key: string
  value: string
  // 空の値 ([未取得]・—（理由）) は text.disabled
  isEmpty?: boolean
  source?: Source
}

const sourceOf = (kind: Source['kind'], iso: string | null): Source => ({
  date: formatMonthDay(iso),
  kind,
})

// 日付は 5ch (= 10/14) の右揃え. 9/30 と 10/14 で種類の語の x がずれないようにする.
const SourceCell = ({ source }: { source?: Source }) => (
  <MetaText sx={{ whiteSpace: 'nowrap', ...FONT_TABULAR }}>
    {source && (
      <>
        <Box
          component="span"
          sx={{ display: 'inline-block', minWidth: '5ch', textAlign: 'right' }}
        >
          {source.date ?? ''}
        </Box>{' '}
        {source.kind}
      </>
    )}
  </MetaText>
)

// 1 属性の行. 頭書き → 会話で取れたもの → 会話で「ない」と答えたもの の順.
// 頭書きと会話が食い違っても片方を消さない (yakureki-back log/262 決定 10).
// 「なし」は属性の状態にせず、 問いごとの行で出す (同 work-log/10 §5).
const linesOf = (
  attribute: PatientAttribute,
  patient: Props['patient']
): Line[] => {
  const reason = notApplicableReason(attribute, patient)
  if (reason) return [{ key: 'na', value: `—（${reason}）`, isEmpty: true }]

  const { headnote, items, confirmedNone } = attribute
  if (headnote.length + items.length + confirmedNone.length === 0) {
    return [{ key: 'unknown', value: '[未取得]', isEmpty: true }]
  }
  return [
    ...headnote.map((h, i) => ({
      key: `headnote-${i}`,
      value: h.text,
      source: sourceOf('頭書き', h.checkedAt),
    })),
    ...items.map((it, i) => ({
      key: `item-${i}`,
      value: it.drug ? `${it.value}（${it.drug}）` : it.value,
      source: sourceOf('会話', it.observedOn),
    })),
    ...confirmedNone.map((n, i) => ({
      key: `none-${i}`,
      value: `${n.value} なし`,
      source: sourceOf('会話', n.observedOn),
    })),
  ]
}

// 監査の面 2「患者の属性」. back が 7 つを固定の順で返すので、 並べ替えない.
// 3 列 = 見出し 7.5em / 値 / 出所. 属性ごとの group は subgrid で外の列に乗せ、
// 出所の列を全行で同じ x に揃える.
export const PatientAttributeTable = ({ attributes, patient }: Props) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '7.5em minmax(0, 1fr) auto',
      columnGap: 1.5,
      rowGap: 0.75,
    }}
  >
    {attributes.map((a) => {
      const lines = linesOf(a, patient)
      return (
        <Box
          key={a.key}
          role="group"
          aria-label={a.label}
          sx={{
            display: 'grid',
            gridColumn: '1 / -1',
            gridTemplateColumns: 'subgrid',
            rowGap: 0.25,
            alignItems: 'baseline',
          }}
        >
          <MetaText sx={{ gridRow: `span ${lines.length}` }}>
            {a.label}
          </MetaText>
          {lines.map((line) => (
            <Fragment key={line.key}>
              {line.isEmpty ? (
                <EmptyLine>{line.value}</EmptyLine>
              ) : (
                <BodyText>{line.value}</BodyText>
              )}
              <SourceCell source={line.source} />
            </Fragment>
          ))}
        </Box>
      )
    })}
  </Box>
)
